/**
 * ClientPreviewDialog — ★ Fase 8.W: anteprima "occhi del cliente".
 * Replica fedelmente lo Step 1 della pagina pubblica di firma (/firma/:token)
 * con i valori compilati, gli allegati selezionati e il pulsante di avanzamento,
 * così chi invia vede ESATTAMENTE ciò che vedrà il firmatario.
 */
import React from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { SafeHtml } from '../../lib/sanitize';
import {
  ShieldCheck, Mail, IdCard, PenLine, FileSignature, ChevronRight,
  FileText, Download, Eye, X,
} from 'lucide-react';

const STEPS = [
  { key: 'read', label: 'Documento', icon: FileSignature },
  { key: 'otp', label: 'Verifica email', icon: Mail },
  { key: 'identity', label: 'Identità', icon: IdCard },
  { key: 'sign', label: 'Firma', icon: PenLine },
];

function PreviewStepper() {
  return (
    <div className="flex items-center justify-center gap-1 md:gap-2 flex-wrap">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] md:text-xs font-semibold ${
            i === 0 ? 'bg-[#0C3C2D] text-[#E8C874]' : 'bg-slate-100 text-slate-400'
          }`}>
            <s.icon className="w-3.5 h-3.5" />
            {s.label}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export function ClientPreviewDialog({
  open,
  onClose,
  templateName,
  recipientName,
  senderName,
  contentHtml,
  attachments = [],
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-4xl w-[96vw] max-h-[94vh] p-0 gap-0 overflow-hidden bg-[#f4f1e8]"
        data-testid="client-preview-dialog"
      >
        <DialogTitle className="sr-only">Anteprima cliente</DialogTitle>

        {/* Banner anteprima */}
        <div className="flex items-center justify-between gap-2 bg-[#E8C874] text-[#0C3C2D] px-4 py-2 text-xs font-bold uppercase tracking-widest">
          <span className="flex items-center gap-2">
            <Eye className="w-4 h-4" /> Anteprima — così lo vedrà il cliente
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-[#0C3C2D]/10"
            aria-label="Chiudi anteprima"
            data-testid="client-preview-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: 'calc(94vh - 40px)' }}>
          {/* Header identico alla pagina di firma */}
          <header className="bg-[#0C3C2D] text-white">
            <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
              <div className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#E8C874] font-semibold">
                Green Paradise · HCF
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-white/70">
                <ShieldCheck className="w-4 h-4 text-[#E8C874]" /> Firma elettronica protetta
              </div>
            </div>
          </header>

          <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-5">
            <div className="text-center space-y-2">
              <h1 className="font-serif text-2xl md:text-3xl text-[#0C3C2D]" data-testid="client-preview-title">
                {templateName || 'Contratto'}
              </h1>
              <p className="text-sm text-slate-500">
                Gentile <b>{recipientName || 'Nome del firmatario'}</b>,
                {' '}{senderName || 'il mittente'} ti ha inviato questo documento per la firma.
              </p>
              <PreviewStepper />
            </div>

            {/* Documento */}
            <div
              className="bg-white rounded-2xl border border-[#e2ddcc] p-5 md:p-8 prose prose-sm max-w-none"
              data-testid="client-preview-content"
            >
              <SafeHtml html={contentHtml || '<p><i>Nessun contenuto…</i></p>'} />
            </div>

            {/* Allegati */}
            {attachments.length > 0 && (
              <div className="bg-white rounded-2xl border border-[#e2ddcc] p-4 md:p-5" data-testid="client-preview-attachments">
                <div className="text-xs font-semibold text-[#0C3C2D] uppercase tracking-wide mb-2">
                  Allegati al contratto ({attachments.length})
                </div>
                <div className="space-y-1.5">
                  {attachments.map((a, i) => (
                    <div
                      key={`${a.name}-${i}`}
                      className="flex items-center gap-2 rounded-lg border border-[#e2ddcc] px-3 py-2 text-sm text-[#0C3C2D]"
                      data-testid={`client-preview-attachment-${i}`}
                    >
                      <FileText className="w-4 h-4 shrink-0" />
                      <span className="truncate flex-1">{a.name}</span>
                      <Download className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-2">
                  Ti consigliamo di leggere gli allegati prima di procedere alla firma.
                </p>
              </div>
            )}

            {/* Pulsante (solo visivo) */}
            <Button
              className="w-full h-12 bg-[#0C3C2D] text-[#E8C874] text-base cursor-default opacity-90 hover:bg-[#0C3C2D]"
              disabled
              data-testid="client-preview-continue-btn"
            >
              Ho letto il documento — procedi alla firma <ChevronRight className="w-4 h-4 ml-1" />
            </Button>

            <p className="text-center text-[10px] text-slate-400 pb-4">
              Dopo questo passaggio il cliente verificherà la sua email (codice OTP),
              caricherà documento + selfie e firmerà con firma disegnata.
            </p>
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
